import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { applyTheme, GRAPHIKPRO_TOKENS } from "@/context/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Bike, Zap, Loader2, Camera, CheckCircle2, Clock, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong");

const STATUS = {
  payment: { label: "Awaiting payment", cls: "bg-status-overdue/15 text-status-overdue", icon: Clock },
  payment_review: { label: "Payment under review", cls: "bg-status-rented/15 text-status-rented", icon: Clock },
  handover: { label: "Confirmed — collect your bike", cls: "bg-status-available/15 text-status-available", icon: CheckCircle2 },
  active: { label: "Ride active", cls: "bg-status-available/15 text-status-available", icon: CheckCircle2 },
};

function StatusPill({ stage }) {
  const s = STATUS[stage] || { label: stage, cls: "bg-muted text-muted-foreground", icon: Clock };
  const Icon = s.icon;
  return <span data-testid="booking-status" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${s.cls}`}><Icon className="h-3.5 w-3.5" /> {s.label}</span>;
}

export default function PublicBooking() {
  const { subdomain } = useParams();
  const [op, setOp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [step, setStep] = useState("browse"); // browse | form | pay | status | track
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", id_type: "aadhaar", id_number: "", selfie_captured: false, accept_terms: false });
  const [result, setResult] = useState(null);
  const [token, setToken] = useState(null);
  const [myBookings, setMyBookings] = useState([]);
  const [upiRef, setUpiRef] = useState("");
  const [track, setTrack] = useState({ phone: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/operators/${subdomain}`);
        setOp(data); applyTheme(data.branding?.tokens || GRAPHIKPRO_TOKENS);
        const v = await axios.get(`${API}/public/operators/${subdomain}/vehicles`);
        setVehicles(v.data);
      } catch { setNotFound(true); }
    })();
  }, [subdomain]);

  const refreshStatus = useCallback(async (tok) => {
    try {
      const { data } = await axios.get(`${API}/tourist/bookings`, { headers: { Authorization: `Bearer ${tok}` } });
      setMyBookings(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (step === "status" && token) {
      refreshStatus(token);
      timer.current = setInterval(() => refreshStatus(token), 6000);
      return () => clearInterval(timer.current);
    }
  }, [step, token, refreshStatus]);

  const submitBooking = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/operators/${subdomain}/bookings`, {
        ...form, vehicle_id: picked.id,
        window_start: new Date().toISOString(),
        window_end: new Date(Date.now() + 86400000).toISOString(),
      });
      setResult(data); setToken(data.token); setStep("pay");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tourist/bookings/${result.booking_id}/mark-paid`, { upi_reference: upiRef },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Marked as paid — the operator will verify and confirm shortly");
      setStep("status");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doTrack = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/operators/${subdomain}/tourist-login`, track);
      setToken(data.token); setStep("status");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (notFound) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Operator not found.</div>;
  if (!op) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const upiLink = result?.payment?.upi_vpa
    ? `upi://pay?pa=${result.payment.upi_vpa}&pn=${encodeURIComponent(result.payment.payee_name || op.name)}&am=${result.amount_due}&cu=INR&tn=${result.booking_ref}`
    : null;

  return (
    <div className="min-h-screen bg-tenant-bg">
      <header className="border-b border-tenant-border bg-tenant-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-tenant-button bg-tenant-primary text-tenant-primary-foreground"><Zap className="h-5 w-5" /></div>
            <div><p className="text-sm font-semibold leading-tight">{op.name}</p><p className="text-xs text-muted-foreground">{op.branding?.tagline || "Rent a bike"}</p></div>
          </div>
          {step === "browse" && <Button data-testid="track-booking-btn" variant="ghost" size="sm" onClick={() => setStep("track")}>Track booking</Button>}
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-5 py-8">
        {!op.accepting_bookings && (
          <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center text-sm text-muted-foreground">This operator is not accepting online bookings right now.</div>
        )}

        {op.accepting_bookings && step === "browse" && (
          <>
            <div><h1 className="text-3xl font-semibold tracking-tight">Book your ride</h1><p className="mt-1 text-sm text-muted-foreground">Pick a bike, pay by UPI, ride away. No account needed.</p></div>
            {vehicles.length === 0 ? (
              <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center text-sm text-muted-foreground">No bikes available right now. Please check back soon.</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {vehicles.map((v) => (
                  <div key={v.id} data-testid={`book-vehicle-${v.reg_number}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
                    <div className="flex items-start justify-between">
                      <div><p className="font-semibold">{v.model}</p><p className="text-sm text-muted-foreground">{v.reg_number} · {v.type}</p></div>
                      <Bike className="h-5 w-5 text-tenant-primary" />
                    </div>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{v.charge_pct != null ? `${v.charge_pct}% charge` : "Ready"}</span>
                      <span className="font-medium">₹{v.deposit_estimate?.toLocaleString()} deposit</span>
                    </div>
                    <Button data-testid={`pick-${v.reg_number}`} className="mt-4 w-full bg-tenant-primary text-tenant-primary-foreground hover:opacity-90" onClick={() => { setPicked(v); setStep("form"); }}>Book this</Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === "form" && picked && (
          <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-6 shadow-tenant-soft">
            <button onClick={() => setStep("browse")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
            <h2 className="text-xl font-semibold">{picked.model} · {picked.reg_number}</h2>
            <p className="mb-5 text-sm text-muted-foreground">A few quick details to reserve your bike.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="tourist-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="tourist-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>ID type</Label>
                  <select data-testid="tourist-id-type" value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="aadhaar">Aadhaar</option><option value="passport">Passport</option><option value="dl">Driving licence</option>
                  </select>
                </div>
                <div className="space-y-1.5"><Label>ID number</Label><Input data-testid="tourist-id-number" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} /></div>
              </div>
              <Button data-testid="tourist-selfie" type="button" variant={form.selfie_captured ? "default" : "outline"} className="w-full gap-2" onClick={() => setForm({ ...form, selfie_captured: true })}>
                <Camera className="h-4 w-4" /> {form.selfie_captured ? "Selfie captured ✓" : "Capture selfie"}
              </Button>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox data-testid="tourist-terms" checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: !!v })} />
                <span className="text-muted-foreground">I accept the rental terms, insurance acknowledgement and agree to return the bike in good condition.</span>
              </label>
              <Button data-testid="tourist-book-submit" disabled={busy} onClick={submitBooking} className="w-full gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reserve &amp; continue to payment
              </Button>
            </div>
          </div>
        )}

        {step === "pay" && result && (
          <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-6 text-center shadow-tenant-soft">
            <h2 className="text-xl font-semibold">Pay ₹{result.amount_due?.toLocaleString()}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Deposit ₹{result.deposit?.toLocaleString()} + advance ₹{result.advance?.toLocaleString()}. Scan with any UPI app.</p>
            {upiLink ? (
              <div className="mx-auto mt-5 w-fit rounded-tenant-card border border-tenant-border bg-white p-4"><QRCodeSVG value={upiLink} size={200} /></div>
            ) : (
              <p className="mt-5 text-sm text-status-overdue">This operator hasn't set up a UPI ID yet. Please pay at the counter.</p>
            )}
            <p className="mt-3 text-sm">{result.payment?.payee_name} · <span className="font-mono">{result.payment?.upi_vpa || "—"}</span></p>
            <div className="mt-3 rounded-tenant-button bg-muted px-3 py-2 text-sm">Booking ref <span className="font-mono font-semibold">{result.booking_ref}</span> · PIN <span className="font-mono font-semibold">{result.pin}</span><br /><span className="text-xs text-muted-foreground">Save these to track your booking</span></div>
            <div className="mt-5 space-y-2 text-left">
              <Label>UPI reference / transaction ID (optional)</Label>
              <Input data-testid="tourist-upi-ref" value={upiRef} onChange={(e) => setUpiRef(e.target.value)} placeholder="e.g. 4012XXXXXX" />
            </div>
            <Button data-testid="tourist-mark-paid" disabled={busy} onClick={markPaid} className="mt-4 w-full gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} I've paid
            </Button>
          </div>
        )}

        {step === "status" && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Your bookings</h2>
            {myBookings.length === 0 ? <p className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</p> : myBookings.map((b) => (
              <div key={b.id} data-testid={`my-booking-${b.booking_ref}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
                <div className="flex items-start justify-between gap-3">
                  <div><p className="font-semibold">{b.vehicle_model} · {b.vehicle_reg}</p><p className="text-sm text-muted-foreground">Ref {b.booking_ref} · ₹{b.amount_due?.toLocaleString()}</p></div>
                  <StatusPill stage={b.stage} />
                </div>
                {b.stage === "handover" && <p className="mt-3 flex items-center gap-1.5 text-sm text-status-available"><ShieldCheck className="h-4 w-4" /> Payment confirmed. Visit the counter to collect your bike.</p>}
                {b.stage === "payment_review" && <p className="mt-3 text-sm text-muted-foreground">We're verifying your payment. This page updates automatically.</p>}
              </div>
            ))}
          </div>
        )}

        {step === "track" && (
          <div className="mx-auto max-w-sm rounded-tenant-card border border-tenant-border bg-tenant-surface p-6 shadow-tenant-soft">
            <button onClick={() => setStep("browse")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
            <h2 className="text-xl font-semibold">Track your booking</h2>
            <p className="mb-4 text-sm text-muted-foreground">Enter the phone & PIN from your booking.</p>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="tourist-track-phone" value={track.phone} onChange={(e) => setTrack({ ...track, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>PIN</Label><Input data-testid="tourist-track-pin" value={track.pin} onChange={(e) => setTrack({ ...track, pin: e.target.value })} /></div>
              <Button data-testid="tourist-track-submit" disabled={busy} onClick={doTrack} className="w-full gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">{busy && <Loader2 className="h-4 w-4 animate-spin" />} View status</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
